import React from 'react'
import styled, { css, DefaultTheme, StyledComponent } from 'styled-components'
import { getDisplayName } from '@sketch/utils'
import {
  top,
  left,
  right,
  bottom,
  LeftProps,
  TopProps,
  RightProps,
  BottomProps,
  zIndex,
  ZIndexProps,
} from 'styled-system'

export interface AbsoluteProps
  extends LeftProps,
    TopProps,
    RightProps,
    BottomProps,
    ZIndexProps {
  position?: never
}

const absoluteCss = css`
  position: absolute;
  ${left}
  ${top}
  ${right}
  ${bottom}
  ${zIndex}
`

export function withAbsolute<C extends React.ElementType>(Comp: C) {
  /**
   * We are passing `any` to styled here to avoid increased TypeScript complexity.
   * The original typing should be:
   * ```
   *   styled(Comp)<AbsoluteProps>` ... `
   * ```
   *
   * However, this causes TypeScript to bloat and slow down significantly to the point that the whole
   * process crashes on some machines/projects (Particularly CI).
   *
   * As a workaround, we are using `any` here and casting the final result to
   * `StyledComponent<C, DefaultTheme, AbsoluteProps, never>`, which is the expected type.
   *
   * This way, we avoid TypeScript bloat in this file.
   */
  const styledComp = styled(Comp)<any>`
    ${absoluteCss}
  ` as StyledComponent<C, DefaultTheme, AbsoluteProps, never>

  styledComp.displayName = `withAbsolute(${getDisplayName(Comp)})`
  return styledComp
}
